/**
 * Componente DealCard - Card para exibição no Kanban de Deals
 * Suporta drag-and-drop via @dnd-kit
 * 
 * Versão: 1.7.0
 * - v1.7.0: Menu de ações respeita permissões granulares por fase (workspace):
 *   "Editar" só aparece quando status='open' E canEditDeal. "Marcar Ganho/Perdido"
 *   só aparece quando status='open' E canChangePhase. "Reabrir Negócio" só aparece
 *   quando status!='open' E canReopenDeal. Alinha card com o modal/drag.
 * - v1.6.0: Layout ajustado: Valor + Origem na mesma linha
 * - Data + Prioridade + Probabilidade na mesma linha
 * - Tags sem botão + (movido para rodapé)
 * - Rodapé: Vendedor à esquerda + ícones (tel, whatsapp, tag) à direita
 * - Origem agora exibe label padronizado via getLeadSourceLabel
 * - v1.5.0: Correção de overflow - linha Valor+Origem e Tags agora truncam corretamente
 * - v1.6.0: Correção definitiva de overflow - containers com min-w-0 + overflow-hidden,
 *   Status badge com shrink-0, LeadSourceBadge sem shrink-0, ícones com shrink-0
 */
import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Building2,
  Calendar,
  MoreHorizontal,
  Trophy,
  XCircle,
  Pencil,
  Eye,
  RotateCcw,
  Flag,
  Tag,
  Phone,
  MessageCircle,
  FileSignature } from
'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { stripHtml } from '@/lib/sanitize';
import { CrmDeal, DealPriority } from '@/hooks/useCrmDeals';
import { useDealTagAssignments } from '@/hooks/useDealTags';
import { type BatchDealTagAssignment } from '@/hooks/useColumnDealTags';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger } from
'@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import { DealTagBadge } from '@/components/crm/deals/tags/DealTagBadge';
import { AssignDealTagsDialog } from '@/components/crm/deals/tags/AssignDealTagsDialog';
import { DealStatusBadge } from '@/components/crm/DealStatusBadge';
import { DealAgeIndicator } from '@/components/crm/deals/DealAgeIndicator';
import { ListChecks } from 'lucide-react';
import { LeadSourceBadge } from '@/components/crm/deals/components/LeadSourceBadge';
import { CONTRACT_STATUS_LABELS, ContractStatus } from '@/types/contracts';
import type { ColumnSortField } from '@/components/crm/deals/ColumnSortDialog';
import { StartChatInboxPicker } from '@/components/crm/deals/StartChatInboxPicker';

// Props do componente
interface DealCardProps {
  deal: CrmDeal;
  isDragging?: boolean;
  onEdit?: (deal: CrmDeal) => void;
  onView?: (deal: CrmDeal) => void;
  onMarkWon?: (deal: CrmDeal) => void;
  onMarkLost?: (deal: CrmDeal) => void;
  onReopen?: (deal: CrmDeal) => void;
  /** Contagem de mensagens não lidas do contato (filtrada por inboxes acessíveis) */
  unreadCount?: number;
  /** ID da última conversa do contato para abrir chat direto */
  lastConversationId?: string | null;
  /** Callback para abrir o modal de chat com a conversa */
  onOpenChat?: (conversationId: string) => void;
  /** Callback para abrir modal de cliente (customer) */
  onViewCustomer?: (customerId: string) => void;
  /**
   * Callback para abrir modal 360º do contato (quando não há cliente vinculado).
   * v1.9.0: envia também um "preview" com os dados já conhecidos pelo card
   * (nome/telefone/e-mail/foto) para a ficha abrir preenchida na hora.
   */
  onViewContact?: (contactId: string, preview?: {
    id: string;
    name?: string | null;
    phone?: string | null;
    email?: string | null;
    avatar_url?: string | null;
  }) => void;
  /** Tags pré-carregadas em batch pela coluna (evita N queries individuais) */
  batchTagAssignments?: BatchDealTagAssignment[];
  /** v2.3.0: Campo de ordenação selecionado — controla qual data exibir no DealAgeIndicator */
  sortField?: ColumnSortField;
  /**
   * v1.7.0: Indica se o card pode ser arrastado entre fases.
   * - `true` (default): comportamento atual (drag habilitado, grip visível).
   * - `false`: granular ON + sem `can_change_phase` na fase → desabilita useSortable
   *   e oculta o handle de arrasto. Mantém todas as demais ações do card intactas.
   */
  canDrag?: boolean;
  /** v2.5.0: Callback ao clicar no badge de checklist do playbook (per_phase) */
  onOpenActivity?: (dealId: string) => void;
  /**
   * v1.7.0: Permissões granulares aplicadas ao menu de ações do card.
   * Devem refletir a mesma matriz usada pelo modal/drag (workspace-scoped):
   * - `canEditDeal`: controla item "Editar" (requer status='open').
   * - `canChangePhase`: controla itens "Marcar como Ganho/Perdido" (requer status='open').
   * - `canReopenDeal`: controla item "Reabrir Negócio" (requer status != 'open').
   * Default `true` preserva o comportamento legado para outros consumidores (dashboards, etc).
   */
  canEditDeal?: boolean;
  canChangePhase?: boolean;
  canReopenDeal?: boolean;
}


// Configuração de cores por prioridade (cores fortes, sem tons pastéis)
const priorityConfig: Record<DealPriority, {flagColor: string;textColor: string;label: string;}> = {
  low: {
    flagColor: '#64748b', // slate-500
    textColor: 'text-slate-600 dark:text-slate-400',
    label: 'Baixa'
  },
  medium: {
    flagColor: '#3b82f6', // blue-500
    textColor: 'text-blue-600 dark:text-blue-400',
    label: 'Média'
  },
  high: {
    flagColor: '#f97316', // orange-500
    textColor: 'text-orange-600 dark:text-orange-400',
    label: 'Alta'
  },
  urgent: {
    flagColor: '#ef4444', // red-500
    textColor: 'text-red-600 dark:text-red-400',
    label: 'Urgente'
  }
};

/**
 * Componente de Card para Deal no Kanban
 */
export function DealCard({
  deal,
  isDragging = false,
  onEdit,
  onView,
  onMarkWon,
  onMarkLost,
  onReopen,
  unreadCount = 0,
  lastConversationId,
  onOpenChat,
  onViewCustomer,
  onViewContact,
  batchTagAssignments,
  sortField = 'created_at',
  canDrag = true,
  onOpenActivity,
  canEditDeal = true,
  canChangePhase = true,
  canReopenDeal = true,
}: DealCardProps) {
  // Estado para dialog de tags
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  // Controla o Popover de seleção de inbox para iniciar nova conversa
  const [startChatOpen, setStartChatOpen] = useState(false);

  // Query de tags do deal - só executa se NÃO recebeu tags via prop batch
  const { data: hookTagAssignments = [] } = useDealTagAssignments(
    batchTagAssignments ? undefined : deal.id
  );

  // Usa tags da prop batch se disponível, senão fallback para hook individual
  // Converte BatchDealTagAssignment para formato compatível com DealTagBadge
  const tagAssignments = batchTagAssignments
    ? batchTagAssignments.map(a => ({
        deal_id: a.deal_id,
        tag_id: a.tag_id,
        assigned_by: a.assigned_by,
        assigned_at: a.assigned_at,
        tag: { name: a.tag_name, color: a.tag_color },
      }))
    : hookTagAssignments;

  // Configuração do sortable (drag-and-drop)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging
  } = useSortable({
    id: deal.id,
    // Quando canDrag=false, dnd-kit ignora completamente este item (sem listeners ativos)
    disabled: !canDrag,
    data: {
      type: 'deal',
      deal,
      phaseId: deal.phase_id
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const dragging = isDragging || isSortableDragging;

  // Configuração de prioridade do deal
  const priority = priorityConfig[deal.priority];

  // v1.8.3: Truncar nome do contato/empresa via JavaScript para evitar overflow
  // Limite de 25 caracteres para equilíbrio entre espaço e legibilidade
  const rawContactName = deal.company_name || deal.contact?.name || '';
  const displayContactName = rawContactName.length > 25 ?
  rawContactName.slice(0, 25) + '...' :
  rawContactName;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        // v1.4.0: Removida coloração de fundo por status - mantém card branco padrão
        // O badge de status já indica visualmente se o deal é ganho/perdido
        // v1.7.0: Adicionado w-full para forçar largura 100% do container pai e evitar overflow
        'group w-full max-w-full bg-card border rounded-lg p-3 shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden',
        dragging && 'opacity-60 shadow-xl scale-[1.02] rotate-1'
      )}>

      {/* Header com grip e menu */}
      <div className="flex items-start justify-between mb-2 gap-0">
        <div className="flex items-start gap-1.5 flex-1 min-w-0 overflow-hidden">
          {/* Handle de drag — oculto quando o usuário não tem permissão `can_change_phase` na fase */}
          {canDrag && (
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing touch-none opacity-40 hover:opacity-100 group-hover:opacity-70 transition-opacity shrink-0 mt-0.5 p-0.5 -ml-0.5 rounded hover:bg-muted/50">

              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </button>
          )}

          {/* Título */}
          <h4
            className="font-semibold text-[11px] truncate flex-1 min-w-0 overflow-hidden leading-snug"
            onClick={() => onView?.(deal)}
            title={deal.title}>

            {deal.title && deal.title.length > 25 ? deal.title.slice(0, 25) + '...' : deal.title}
          </h4>
        </div>

        {/* Menu de ações */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 hover:bg-muted">

              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* Ver Detalhes — sempre disponível (somente leitura) */}
            <DropdownMenuItem onClick={() => onView?.(deal)}>
              <Eye className="h-4 w-4 mr-2" />
              Ver Detalhes
            </DropdownMenuItem>

            {/* Editar — apenas para negócios abertos e com permissão de edição na fase */}
            {deal.status === 'open' && canEditDeal && (
              <DropdownMenuItem onClick={() => onEdit?.(deal)}>
                <Pencil className="h-4 w-4 mr-2" />
                Editar
              </DropdownMenuItem>
            )}

            {/* Separador apenas se há alguma ação de status para mostrar abaixo */}
            {((deal.status === 'open' && canChangePhase) ||
              (deal.status !== 'open' && canReopenDeal)) && <DropdownMenuSeparator />}

            {deal.status === 'open' ? (
              <>
                {/* Marcar Ganho/Perdido — segue mesma regra do drag (can_change_phase) */}
                {canChangePhase && (
                  <>
                    <DropdownMenuItem
                      onClick={() => onMarkWon?.(deal)}
                      className="text-green-600">
                      <Trophy className="h-4 w-4 mr-2" />
                      Marcar como Ganho
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onMarkLost?.(deal)}
                      className="text-red-600">
                      <XCircle className="h-4 w-4 mr-2" />
                      Marcar como Perdido
                    </DropdownMenuItem>
                  </>
                )}
              </>
            ) : (
              /* Reabrir Negócio — requer permissão granular can_reopen na fase atual */
              canReopenDeal && (
                <DropdownMenuItem onClick={() => onReopen?.(deal)}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reabrir Negócio
                </DropdownMenuItem>
              )
            )}
          </DropdownMenuContent>

        </DropdownMenu>
      </div>

      {/* Empresa/Contato + Status - v1.6.0: min-w-0 + overflow-hidden para conter elementos */}
      <div className="flex items-center justify-between mb-2 min-w-0 overflow-hidden mx-0 gap-0">
        {/* Empresa/Contato à esquerda */}
        {/* v1.7.2: max-w-[45%] para limitar ainda mais a largura do nome */}
        {displayContactName ?
        <div 
          className={cn(
            "flex items-center gap-1.5 text-xs text-muted-foreground min-w-0 flex-1 max-w-[65%]",
            // Interativo se tem cliente OU contato vinculado (com o respectivo callback)
            ((deal.customer_id && onViewCustomer) || (deal.contact?.id && onViewContact)) &&
              "cursor-pointer hover:text-primary transition-colors"
          )}
          onClick={(e) => {
            // Prioriza o modal de cliente; se não houver cliente, abre o do contato
            if (deal.customer_id && onViewCustomer) {
              e.stopPropagation();
              onViewCustomer(deal.customer_id);
            } else if (deal.contact?.id && onViewContact) {
              e.stopPropagation();
              // v1.9.0: envia preview com os dados já carregados no card,
              // para a ficha do contato abrir preenchida instantaneamente
              onViewContact(deal.contact.id, {
                id: deal.contact.id,
                name: deal.contact.name ?? displayContactName ?? null,
                phone: deal.contact.phone ?? null,
                email: deal.contact.email ?? null,
                avatar_url: deal.contact.avatar_url ?? null,
              });
            }
          }}
        >
            <Building2 className="h-3 w-3 shrink-0" />
            <span className="truncate">
              {displayContactName}
            </span>
          </div> :

        <div className="flex-1" />
        }
        
        {/* Status à direita - sempre visível - v1.6.0: shrink-0 para manter tamanho */}
        <div className="cursor-pointer" onClick={(e) => { e.stopPropagation(); onView?.(deal); }}>
          <DealStatusBadge status={deal.status} className="shrink-0" />
        </div>
      </div>

      {/* Descrição - v1.7.0: sanitiza HTML e trunca texto */}
      {(() => {
        const rawDesc = deal.description_html || deal.description;
        const clean = rawDesc ? stripHtml(rawDesc) : '';
        if (!clean) return null;
        return (
          <p className="text-xs text-muted-foreground mb-2 line-clamp-2 break-all cursor-pointer" onClick={(e) => { e.stopPropagation(); onView?.(deal); }}>
            {clean.length > 80 ? clean.slice(0, 80) + '...' : clean}
          </p>
        );
      })()}

      {/* Valor + Origem (na mesma linha) - v1.5.0: min-w-0 + overflow-hidden para conter elementos */}
      <div className="flex items-center justify-between mb-2 min-w-0 overflow-hidden gap-0 cursor-pointer" onClick={(e) => { e.stopPropagation(); onView?.(deal); }}>
        {/* v1.5.0: Container flex-1 min-w-0 permite que PriceDisplay encolha se necessário */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <PriceDisplay
            amount={deal.value}
            currency={deal.currency}
            size="sm"
            truncate />

        </div>
        
        {/* Origem do negócio — exibe o nome completo (sem max-w) e usa
            tamanho `xs` (~10% menor que `sm`) para liberar espaço horizontal. */}
        <LeadSourceBadge sourceId={deal.source} size="xs" className="shrink-0" />
      </div>

      {/* Data + Prioridade + Probabilidade (na mesma linha) */}
      <div className="flex items-center gap-3 mb-2 overflow-hidden min-w-0 cursor-pointer" onClick={(e) => { e.stopPropagation(); onView?.(deal); }}>
        {/* Data de criação */}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Calendar className="h-3 w-3" />
          <span>{format(new Date(deal.created_at), 'dd/MM/yy', { locale: ptBR })}</span>
        </div>

        {/* Bandeira + Nome da prioridade */}
        <div className="flex items-center gap-1.5">
          <Flag
            className="h-3.5 w-3.5"
            style={{ color: priority.flagColor }}
            fill={priority.flagColor} />

          <span className={cn('text-xs font-medium', priority.textColor)}>
            {priority.label}
          </span>
        </div>

        {/* Probabilidade */}
        {deal.probability !== null && deal.probability !== undefined &&
        <span className="text-xs text-muted-foreground font-medium">
            {deal.probability}%
          </span>
        }

        {/* v2.5.0: Badge de checklist do playbook (per_phase) — dados vêm do
            get_kanban_column (sem N+1). Clique abre o modal de atividade. */}
        <CadencePhaseBadge
          dealId={deal.id}
          total={deal.cadence_phase_total ?? 0}
          pending={deal.cadence_phase_pending ?? 0}
          hasOverdue={deal.cadence_phase_has_overdue ?? false}
          onClick={onOpenActivity}
        />
      </div>

      {/* Tags do negócio (sem botão +) - v1.5.0: overflow-hidden + max-w nas tags */}
      {tagAssignments.length > 0 &&
      <div className="flex items-center gap-1 flex-wrap mb-2 overflow-hidden">
          {/* Exibe até 2 tags - v1.5.0: max-w-[100px] para limitar largura individual */}
          {tagAssignments.slice(0, 2).map((assignment) =>
        <DealTagBadge
          key={assignment.tag_id}
          name={assignment.tag?.name || ''}
          color={assignment.tag?.color || '#888888'}
          size="sm"
          className="max-w-[100px]" />

        )}

          {/* Badge "+N" para tags adicionais */}
          {tagAssignments.length > 2 &&
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 shrink-0">
              +{tagAssignments.length - 2}
            </Badge>
        }
        </div>
      }


      {/* v1.9.0: Badge do último contrato — exibido entre tags e separator */}
      {deal.last_contract_status && CONTRACT_STATUS_LABELS[deal.last_contract_status as ContractStatus] &&
      <div className="flex items-center gap-1 mb-2 min-w-0 overflow-hidden">
          <FileSignature className="h-3 w-3 text-muted-foreground shrink-0" />
          <span className={cn(
          'text-[10px] font-medium px-1.5 py-0.5 rounded-full truncate max-w-[100px]',
          CONTRACT_STATUS_LABELS[deal.last_contract_status as ContractStatus].color
        )}>
            {CONTRACT_STATUS_LABELS[deal.last_contract_status as ContractStatus].label}
          </span>
          {deal.last_contract_title &&
        <span className="text-[10px] text-muted-foreground truncate max-w-[60px]">
              · {deal.last_contract_title}
            </span>
        }
        </div>
      }

      {/* Separador + Rodapé com vendedor e ícones - v1.6.0: min-w-0 + overflow-hidden */}
      <Separator className="my-2" />
      <div className="flex items-center justify-between gap-2 min-w-0 overflow-visible">
        {/* Vendedor à esquerda */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {deal.seller ?
          <>
              <SmartAvatar
              src={deal.seller.profile?.avatar_url ?? deal.seller.avatar_url}
              name={deal.seller.profile?.full_name ?? deal.seller.name}
              size="xs"
              defaultFallback="V"
              use3DFallback />

              <span className="text-xs text-muted-foreground truncate max-w-[70px]">
                {deal.seller.profile?.full_name ?? deal.seller.name ?? 'Vendedor'}
              </span>

            </> :

          <span className="text-xs text-muted-foreground">Sem vendedor</span>
          }
        </div>

        {/* Ícones à direita: Telefone, WhatsApp, Tag - v1.6.0: shrink-0 para manter visíveis */}
        <div className="flex items-center gap-1.5 shrink-0 overflow-visible">
          {/* Telefone - se contato tiver phone */}
          {deal.contact?.phone &&
          <button
            onClick={(e) => {
              e.stopPropagation();
              window.open(`tel:${deal.contact!.phone}`, '_blank');
            }}
            className="h-5 w-5 rounded-full bg-muted hover:bg-muted-foreground/20 flex items-center justify-center transition-colors"
            title={`Ligar: ${deal.contact.phone}`}>

              <Phone className="h-3 w-3 text-muted-foreground" />
            </button>
          }

          {/* Chat - abre conversa existente OU picker de inbox para iniciar nova
              v1.7.0: sem mais fallback wa.me; quando não há conversa, abre Popover
              com inboxes em que o usuário tem permissão (RPC get_user_accessible_inboxes)
              e cria conversa via get_or_create_conversation, abrindo o
              ChatConversationModal no kanban (sem sair da página). */}
          {deal.contact?.phone && deal.contact?.id && (
            <StartChatInboxPicker
              open={startChatOpen}
              onOpenChange={setStartChatOpen}
              contactId={deal.contact.id}
              contactName={deal.contact.name}
              onConversationReady={(conversationId) => {
                // Reaproveita o mesmo handler usado para conversas já existentes
                onOpenChat?.(conversationId);
              }}
              trigger={
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Se já existe conversa, abre direto o modal de chat
                    if (lastConversationId && onOpenChat) {
                      onOpenChat(lastConversationId);
                    } else {
                      // Sem conversa: abre o picker de inbox
                      setStartChatOpen(true);
                    }
                  }}
                  className="relative overflow-visible h-5 w-5 rounded-full bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 flex items-center justify-center transition-colors"
                  title={lastConversationId ? 'Abrir conversa' : 'Iniciar conversa'}
                >
                  <MessageCircle className="h-3 w-3 text-green-600 dark:text-green-400" />
                  {/* Badge de mensagens não lidas */}
                  {unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 z-10 min-w-[14px] h-[14px] rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center px-0.5 leading-none">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
              }
            />
          )}

          {/* Botão de adicionar/gerenciar tags */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setTagDialogOpen(true);
            }}
            className="h-5 w-5 rounded-full bg-muted hover:bg-muted-foreground/20 flex items-center justify-center transition-colors"
            title="Gerenciar tags">

            <Tag className="h-3 w-3 text-muted-foreground" />
          </button>

          {/* v2.5.0: Badge de checklist movido para a linha de prioridade. */}
          {/* Indicador de idade do deal — v2.3.0: data dinâmica conforme ordenação */}
          <DealAgeIndicator
            createdAt={sortField === 'phase_updated_at' && deal.phase_updated_at
              ? deal.phase_updated_at
              : deal.created_at}
            tooltipLabel={sortField === 'phase_updated_at' ? 'Na fase há' : undefined}
          />
        </div>
      </div>

      {/* Dialog para atribuir tags - renderização condicional para evitar N instâncias ocultas */}
      {tagDialogOpen && (
        <AssignDealTagsDialog
          open={tagDialogOpen}
          onOpenChange={setTagDialogOpen}
          dealId={deal.id}
          tenantId={deal.tenant_id} />
      )}

    </div>);

}

// ============================================================================
// Subcomponente: badge de checklist por fase no card do Kanban (v2.5.0)
// - Recebe os dados via props (vindos de get_kanban_column v2.2.0). Não dispara
//   mais a RPC list_deal_cadence_steps por card (era N+1 no Kanban).
// - Visual minimalista (sem cápsula/borda) — alinhado à linha de prioridade.
// - Clicável: abre o modal de atividade do deal via onClick.
// ============================================================================
interface CadencePhaseBadgeProps {
  dealId: string;
  total: number;
  pending: number;
  hasOverdue: boolean;
  onClick?: (dealId: string) => void;
}

function CadencePhaseBadge({ dealId, total, pending, hasOverdue, onClick }: CadencePhaseBadgeProps) {
  if (total === 0) return null;

  const completedOrSkipped = total - pending;
  const allDone = pending === 0;

  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium transition-opacity hover:opacity-80',
        allDone
          ? 'text-emerald-600 dark:text-emerald-400'
          : hasOverdue
            ? 'text-destructive'
            : 'text-muted-foreground',
        onClick && 'cursor-pointer',
      )}
      title={
        allDone
          ? `Checklist desta fase concluído (${total}/${total}) — clique para abrir atividades`
          : `${completedOrSkipped}/${total} steps concluídos nesta fase${hasOverdue ? ' · há pendências em atraso' : ''} — clique para abrir atividades`
      }
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(dealId);
      }}
    >
      <ListChecks className="h-3 w-3" />
      {completedOrSkipped}/{total}
    </button>
  );
}